import throttle from 'lodash.throttle';
import debounce from 'lodash.debounce';

import classnames from 'classnames/bind';
import EmailValidator from 'email-validator';
import PropTypes from 'prop-types';
import React from 'react';

import InputBox from 'presentation/components/InputBox';
import { isIE, isMobileIos } from 'lib/getCustomerProperties';
import overrideStyle from 'lib/overrideStyle';
import SubmitButtonContainer from 'presentation/containers/SubmitButtonContainer';
import { TypingState } from 'enums/session';

import styles from 'presentation/components/styles/Composition.css';
let cx = classnames.bind(styles);

// Typing event is throttled to 1/5sec, stop typing event debounced every start typing call
// Stop typing timeout must be at least 500ms longer to allow time for next typing update
const START_TYPING_INTERVAL = 5000;
const STOP_TYPING_TIMEOUT = 5500;

class Composition extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isValidEmail: true };
    this.isMobileIos = isMobileIos();

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.addTouchHandler = this.addTouchHandler.bind(this);
    this.removeTouchHandler = this.removeTouchHandler.bind(this);
    this.handleTouchEvent = this.handleTouchEvent.bind(this);
    this.enableTouchScrolling = this.enableTouchScrolling.bind(this);
    this.disableTouchScrolling = this.disableTouchScrolling.bind(this);
    this.removeFocus = this.removeFocus.bind(this);
    this.focusComposition = this.focusComposition.bind(this);
    this.preventScrollingFn = ev => ev.preventDefault();
  }

  componentWillMount() {
    this.handleActiveTyping = throttle(this.handleTypingStart, START_TYPING_INTERVAL, { trailing: false });
    this.handleTypingTimeout = debounce(this.handleTypingStop, STOP_TYPING_TIMEOUT);
  }

  componentDidMount() {
    if (this.isMobileIos) {
      this.addTouchHandler();
      if (!this.props.isOnboarding || !this.props.hasUserEmail) {
        /* Do not auto focus when onboarding is disabled. When onboarding is disabled, it's possible mobile iOS devices will have the keyboard hide the first message. Do not auto focus the input field when reopening the composition. This causes the keyboard to open on top of the latest messages. Manually focusing the composition after being mounted is handled correctly.
        Also, do not auto focus if there is no user email set since this mean onboarding is disabled and this composition is the first composition to render. this.props.isOnboarding is not actually reflecting if onboarding is enabled. Prevent auto focus when onboarding is disabled to prevent first message from being hidden by the keyboard */
        return;
      }
    }

    this.focusComposition();
  }

  componentWillUnmount() {
    if (this.isMobileIos) {
      this.removeTouchHandler();
      this.enableTouchScrolling();
    }
  }

  focusComposition() {
    this.input.focus();
    // Must do the following for IE browsers, otherwise customer won't be able to type when the composition is initially focused
    if (isIE()) {
      setTimeout(() => {
        document.activeElement.blur();
        this.input.focus();
      }, 5);
    }
  }

  handleInputChange(ev) {
    this.props.onChange(ev.target.value);
    this.handleActiveTyping();
  }

  handleKeyPress(ev) {
    let eventKey = ev.key;

    if (eventKey === 'Enter' && !ev.shiftKey) {
      this.handleSubmit();
      ev.stopPropagation();
      ev.preventDefault();
      return false;
    }
  }

  handleSubmit() {
    // dont send unless there is at least one non-whitespace character and the chat client is connected
    if (!/\S+/.test(this.props.compositionDraft) || !this.props.isChatClientConnected) {
      return;
    }
    this.handleTypingStop();

    if (this.props.isOnboarding) {
      const email = this.props.compositionDraft.trim();
      const isValidEmail = this.validateEmail(email);
      this.setState({ isValidEmail });
      isValidEmail && this.props.onOnboard(email);
    } else {
      this.props.onSubmit(this.props.compositionDraft);
    }

    this.input.focus();
  }

  handleTypingStart() {
    this.props.onCustomerTypingStateUpdate(TypingState.STARTED);
    this.handleTypingTimeout();
  }

  handleTypingStop() {
    this.props.onCustomerTypingStateUpdate(TypingState.STOPPED);
  }

  addTouchHandler() {
    window.addEventListener('touchstart', this.handleTouchEvent);
  }

  removeTouchHandler() {
    window.removeEventListener('touchstart', this.handleTouchEvent);
  }

  enableTouchScrolling() {
    window.removeEventListener('touchmove', this.preventScrollingFn, { passive: false });
  }

  disableTouchScrolling() {
    window.addEventListener('touchmove', this.preventScrollingFn, { passive: false });
  }

  handleTouchEvent(ev) {
    /* Do not remove focus and close the virtual keyboard when touching something inside the composition */
    if (this.composition.contains(ev.target)) {
      return;
    }
    this.removeFocus();
  }

  removeFocus() {
    document.activeElement.blur();
  }

  validateEmail(email) {
    return EmailValidator.validate(email);
  }

  render() {
    // classname only applies when there there is content in the inputBox
    let submitClasses = cx({ chatSubmitArrow_customColors: /\S+/.test(this.props.compositionDraft) });
    let compositionInputBoxClasses = cx(styles.composition_inputBox, 'composition_inputBox');
    let compositionClassnames = cx(styles.composition, { composition_selfService: this.props.isSelfService });
    let prompt = this.props.isOnboarding ? 'Enter your email' : this.props.prompt;

    return (
      <div className={compositionClassnames} ref={composition => (this.composition = composition)}>
        <div className={styles.composition_wrapper}>
          <pre className={styles.composition_spacer}>
            {this.props.compositionDraft}
            <br />
          </pre>
          {this.props.isOnboarding && !this.state.isValidEmail ? (
            <CompositionEmailError>Please enter a valid email address</CompositionEmailError>
          ) : null}
          <InputBox
            aria-labelledby="gladlyTextBox"
            className={compositionInputBoxClasses}
            data-aid="gladlyChatCompositionInput"
            data-gramm_editor="false"
            onBlur={this.isMobileIos ? this.enableTouchScrolling : null}
            onChange={this.handleInputChange}
            onFocus={this.isMobileIos ? this.disableTouchScrolling : null}
            onKeyPress={this.handleKeyPress}
            prompt={prompt}
            ref={input => (this.input = input)}
            role="textbox"
            value={this.props.compositionDraft}
          />
        </div>
        <SubmitButtonContainer
          aria-label="submitButton"
          role="button"
          className={submitClasses}
          hasContent={!!this.props.compositionDraft}
          onClick={this.handleSubmit}
          onKeyDown={this.handleKeyPress}
          data-aid="gladlyChatCompositionSubmit"
        />
      </div>
    );
  }
}

Composition.propTypes = {
  prompt: PropTypes.string.isRequired,
  isChatClientConnected: PropTypes.bool.isRequired,
  compositionDraft: PropTypes.string.isRequired,
  hasUserEmail: PropTypes.bool,
  isOnboarding: PropTypes.bool,
  isSelfService: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onCustomerTypingStateUpdate: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onOnboard: PropTypes.func.isRequired,
};

export default Composition;

const CompositionEmailError = overrideStyle('span')`
  color: #D2220E;
  font-family: ${p => p.theme.fontLibrary.secondary};
  font-size: 11px;
  left: 20px;
  position: absolute;
  top: 10px;
  z-index: 1;
  `;
